// Shared "Create project" modal — used by the Projects page and by the
// first-run screen (RedirectToFirstProject) shown when the user's org has no
// projects yet, which is the one place the Projects page itself can't be
// reached (all app routes need a project in the URL). Creation is allowed for
// org_admin/platform_admin; the backend 403s anyone else and the message is
// surfaced inline.
import React, { useMemo, useState } from "react";
import { X } from "lucide-react";
import { createProject } from "../../services/projects";

export function ModalShell({ title, onClose, children, footer }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start md:items-center justify-center p-4 overflow-y-auto">
      <div className="bg-card-bg rounded-[8px] shadow-xl w-full max-w-md mt-12 md:mt-0">
        <div className="flex items-center justify-between px-5 py-3 border-b border-page-divider">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-page-bg"><X size={18}/></button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-page-divider">{footer}</div>}
      </div>
    </div>
  );
}

export default function CreateProjectModal({ onClose, onSuccess }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  // Auto-derive slug from name as user types
  React.useEffect(() => {
    if (!slug || slug === slug.toLowerCase().replace(/[^a-z0-9]/g, "_").replace(/^_+|_+$/g, "")) {
      setSlug(name.toLowerCase().replace(/[^a-z0-9]/g, "_").replace(/^_+|_+$/g, "").slice(0, 63));
    }
  }, [name]); // eslint-disable-line

  const valid = useMemo(() => name.trim() && /^[a-z][a-z0-9_]{0,62}$/.test(slug), [name, slug]);
  const submit = async () => {
    if (!valid) return;
    setSubmitting(true); setErr(null);
    try {
      await createProject({ name: name.trim(), slug, description: description.trim() || undefined });
      onSuccess(slug);
    } catch (e) {
      setErr(e?.message || "Failed to create");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title="Create project" onClose={onClose}
      footer={<>
        <button onClick={onClose} className="px-3 py-1.5 rounded text-sm hover:bg-page-bg">Cancel</button>
        <button data-testid="create-project-submit" disabled={!valid || submitting} onClick={submit}
          className="px-3 py-1.5 rounded bg-nav-active text-white text-sm font-medium disabled:opacity-50">
          {submitting ? "Creating…" : "Create"}
        </button>
      </>}>
      <div className="flex flex-col gap-3">
        <label className="text-sm">
          <span className="block text-page-muted mb-1">Name</span>
          <input data-testid="create-project-name" autoFocus value={name} onChange={e=>setName(e.target.value)} className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active"/>
        </label>
        <label className="text-sm">
          <span className="block text-page-muted mb-1">Slug <span className="text-xs">(lowercase, letters/digits/underscores; starts with letter)</span></span>
          <input value={slug} onChange={e=>setSlug(e.target.value)} className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active font-mono text-xs"/>
        </label>
        <label className="text-sm">
          <span className="block text-page-muted mb-1">Description (optional)</span>
          <textarea value={description} onChange={e=>setDescription(e.target.value)} rows={2} className="w-full px-3 py-2 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active"/>
        </label>
        {err && <div className="text-sm text-red-700">{err}</div>}
        <p className="text-xs text-page-muted">Creating a project initializes a new Dolt database with the recipe schema. Takes a few seconds.</p>
      </div>
    </ModalShell>
  );
}
